import React, { FC } from 'react';
import {
  Grid,
  TableHeaderRow,
  TableColumnResizing,
  TableSelection,
  TableFixedColumns,
  TableColumnVisibility,
  TableFilterRow,
} from '@devexpress/dx-react-grid-material-ui';
import { SortingState, SelectionState, IntegratedSelection, FilteringState } from '@devexpress/dx-react-grid';
import { DataTypeProvider } from '@devexpress/dx-react-grid';

import TableWrapper from 'shared/components/tables/TableWrapper';
import useTable from 'shared/hooks/customHooks/useTable';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner';
import { ReactComponent as NoResults } from 'shared/img/icons/no-results.svg';
import { ResourceData, ColumnWidthInfo, SortParams } from '../../types';
import { GOVERNANCE_TAGS_TABLE_COLUMNS } from '../../constants/tableColumns';
import { COLUMN_NAMES } from '../../constants/columnNames';
import {
  SORTING_COLUMN_EXTENSIONS,
  COLUMN_RESIZING_EXTENSIONS,
  FIXED_RIGHT_COLUMNS,
} from '../../constants/tableOptions';
import { CustomHeaderCell, CustomFilterRow } from './customTableComponents';
import { CustomFilterCell, CustomFilterIcon } from 'shared/components/andtComponents/TableComponents/TableComponents';
import { FILTER_OPERATIONS } from 'shared/components/andtComponents/TableComponents/const';
import {
  GovernanceTagIconFormatter,
  GovernanceTagActionFormatter,
  ResourceCostOrNameFormatter,
  UnifiedTagsFormatter,
} from './formatters';
import GovernanceTagsBulkOperations from './GovernanceTagsBulkOperations';

import styles from './GovernanceTagsTableView.module.scss';

const LastUpdateCellFormatter = ({ value }: { value: unknown }) => (
  <span className={styles.lastUpdateCell}>{value as string}</span>
);

const GovernanceTagActionFormatterWrapper: FC<{ onEdit: (resource: ResourceData) => void }> = ({
  onEdit,
  ...props
}: { onEdit: (resource: ResourceData) => void } & Record<string, unknown>) => (
  <GovernanceTagActionFormatter {...props} onEdit={onEdit} />
);

const CustomNoDataComponent = ({ isLoading }: { isLoading: boolean }) => {
  if (isLoading) {
    return null;
  }

  return (
    <td colSpan={100}>
      <div className={styles.noResultsWrapper}>
        <NoResults />
        <span className={styles.mainText}>No resources were found</span>
        <span className={styles.subText}>Try different filters or add resources</span>
      </div>
    </td>
  );
};

const LoadingOverlay: FC = () => (
  <div className={styles.loadingOverlay}>
    <Spinner size={SPINNER_SIZES.MEDIUM} />
  </div>
);

const defaultFilterOperations = [FILTER_OPERATIONS.CONTAINS];
const numericAndDateFilterOperations = [
  FILTER_OPERATIONS.EQUAL,
  FILTER_OPERATIONS.NOT_EQUAL,
  FILTER_OPERATIONS.GREATER_THAN,
  FILTER_OPERATIONS.GREATER_THAN_OR_EQUAL,
  FILTER_OPERATIONS.LESS_THAN,
  FILTER_OPERATIONS.LESS_THAN_OR_EQUAL,
];

interface GovernanceTagsGridProps {
  data: {
    resources: ResourceData[];
    isLoading: boolean;
    selectedResources: (string | number)[];
    expandedRowIds: (string | number)[];
    selectedResourcesData: ResourceData[];
    columnsWidth: ColumnWidthInfo[];
  };
  config: {
    sortConfig: SortParams[];
    hiddenColumnNames: string[];
  };
  filterConfig: {
    showFilters: boolean;
  };
  setters: {
    setExpandedRowIds: (rowIds: (string | number)[]) => void;
    onColumnWidthsChange: (newColumnWidths: ColumnWidthInfo[]) => void;
  };
  handlers: {
    onSortingChange: (newSorting: { columnName: string; direction: 'asc' | 'desc' }[]) => void;
    onSelectionChange: (selection: (string | number)[]) => void;
    onEdit: (resource: ResourceData) => void;
    onAddGovernanceTag: (resources: ResourceData[]) => void;
    onRemoveGovernanceTag: (resources: ResourceData[]) => Promise<void>;
    onFiltersChange?: (filters: Array<{ columnName: string; value: string; operation?: string }>) => void;
  };
  getters: {
    getAdditionalSummaryData: React.ReactNode;
  };
}

const GovernanceTagsTableView: FC<GovernanceTagsGridProps> = ({
  data: { resources, isLoading, selectedResources, expandedRowIds, selectedResourcesData, columnsWidth },
  config: { sortConfig, hiddenColumnNames },
  filterConfig: { showFilters },
  setters: { setExpandedRowIds, onColumnWidthsChange },
  handlers: { onSortingChange, onSelectionChange, onEdit, onAddGovernanceTag, onRemoveGovernanceTag, onFiltersChange },
  getters: { getAdditionalSummaryData },
}) => {
  const { NewTableRow, NewTableSelectionBar } = useTable();

  return (
    <div className={styles.gridContainer}>
      <Grid rows={resources} columns={GOVERNANCE_TAGS_TABLE_COLUMNS}>
        <SortingState
          sorting={sortConfig}
          onSortingChange={onSortingChange}
          columnExtensions={SORTING_COLUMN_EXTENSIONS}
        />
        {showFilters ? <FilteringState onFiltersChange={onFiltersChange} /> : null}
        <SelectionState selection={selectedResources} onSelectionChange={onSelectionChange} />
        <IntegratedSelection />

        <DataTypeProvider
          for={[COLUMN_NAMES.TAGS]}
          formatterComponent={(props) => <UnifiedTagsFormatter {...props} tagField="tags" />}
          availableFilterOperations={defaultFilterOperations}
        />
        <DataTypeProvider
          for={[COLUMN_NAMES.GOVERNANCE_TAGS]}
          formatterComponent={(props) => <UnifiedTagsFormatter {...props} tagField="governanceTags" />}
          availableFilterOperations={defaultFilterOperations}
        />
        <DataTypeProvider
          for={[COLUMN_NAMES.RESOURCE_COST]}
          formatterComponent={(props) => <ResourceCostOrNameFormatter {...props} cost={props.value} />}
          availableFilterOperations={numericAndDateFilterOperations}
        />
        <DataTypeProvider
          for={[COLUMN_NAMES.RESOURCE_NAME]}
          formatterComponent={(props) => <ResourceCostOrNameFormatter {...props} cost={props.value} />}
          availableFilterOperations={defaultFilterOperations}
        />
        <DataTypeProvider for={[COLUMN_NAMES.RESOURCE_ID]} availableFilterOperations={defaultFilterOperations} />
        <DataTypeProvider for={[COLUMN_NAMES.SERVICE]} availableFilterOperations={defaultFilterOperations} />
        <DataTypeProvider for={[COLUMN_NAMES.REGION]} availableFilterOperations={defaultFilterOperations} />
        <DataTypeProvider for={[COLUMN_NAMES.LINKED_ACCOUNT]} availableFilterOperations={defaultFilterOperations} />
        <DataTypeProvider
          for={[COLUMN_NAMES.GOVERNANCE_TAG_ACTION]}
          formatterComponent={(props) => <GovernanceTagActionFormatterWrapper {...props} onEdit={onEdit} />}
          availableFilterOperations={[]}
        />
        <DataTypeProvider
          for={[COLUMN_NAMES.TAG_ICON]}
          formatterComponent={GovernanceTagIconFormatter}
          availableFilterOperations={[]}
        />
        <DataTypeProvider
          for={[COLUMN_NAMES.LAST_UPDATE]}
          formatterComponent={LastUpdateCellFormatter}
          availableFilterOperations={numericAndDateFilterOperations}
        />

        <TableWrapper
          virtual
          stickyHeader
          height={850}
          noDataCellComponent={(props) => <CustomNoDataComponent {...props} isLoading={isLoading} />}
          rowComponent={(props) => (
            <NewTableRow
              className={styles.tableRow}
              spacerHeight={0}
              expandedRowIds={expandedRowIds}
              setExpandedRowIds={setExpandedRowIds}
              {...props}
            />
          )}
        />
        <TableSelection showSelectAll />
        <TableColumnResizing
          resizingMode="widget"
          columnWidths={columnsWidth}
          onColumnWidthsChange={onColumnWidthsChange}
          columnExtensions={COLUMN_RESIZING_EXTENSIONS}
        />
        <TableHeaderRow showSortingControls cellComponent={CustomHeaderCell} />
        {showFilters ? (
          <TableFilterRow
            showFilterSelector
            iconComponent={CustomFilterIcon}
            rowComponent={CustomFilterRow}
            cellComponent={(props) => (
              <CustomFilterCell
                {...props}
                actionColumns={[COLUMN_NAMES.GOVERNANCE_TAG_ACTION, COLUMN_NAMES.TAG_ICON]}
              />
            )}
          />
        ) : null}
        <TableColumnVisibility hiddenColumnNames={hiddenColumnNames} />
        <TableFixedColumns rightColumns={FIXED_RIGHT_COLUMNS} />
        <NewTableSelectionBar
          selectedRows={selectedResourcesData}
          counterClass={styles.counterClass}
          additionalData={getAdditionalSummaryData}
          className={styles.selectionBar}
        >
          <div className={styles.bulkRow}>
            <GovernanceTagsBulkOperations
              selectedResources={selectedResourcesData}
              onAddGovernanceTag={onAddGovernanceTag}
              onRemoveGovernanceTag={onRemoveGovernanceTag}
            />
          </div>
        </NewTableSelectionBar>
      </Grid>

      {isLoading ? <LoadingOverlay /> : null}
    </div>
  );
};

export default GovernanceTagsTableView;
